import React, { useEffect, useState, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import LoadingFallback from "./components/LoadingFallback";

// Componentes de Layout e Lógica (mantêm importação estática)
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AuthenticatedRoute } from './components/AuthenticatedRoute';
import { ScrollToTop } from './components/ScrollToTop';
import { ErrorBoundary } from './pages/errors/ErrorBoundary';
import { RegisterFormProvider } from './pages/Register/hooks/useRegisterForm';
import { useTheme } from './lib/theme';
import { initGA, trackPageView } from './lib/analytics';


// --- Importações Dinâmicas com React.lazy (CORRIGIDAS para export nomeado) ---

// Páginas Públicas
const Home = lazy(() => import('./pages/Home/').then(module => ({ default: module.Home })));
const Login = lazy(() => import('./pages/Login').then(module => ({ default: module.Login })));
const Register = lazy(() => import('./pages/Register').then(module => ({ default: module.Register })));
const NewsDetail = lazy(() => import('./pages/NewsDetail').then(module => ({ default: module.NewsDetail })));
const NewsList = lazy(() => import('./pages/NewsList').then(module => ({ default: module.NewsList })));
const Legislation = lazy(() => import('./pages/Legislation').then(module => ({ default: module.Legislation })));
const Departments = lazy(() => import('./pages/Departments').then(module => ({ default: module.Departments })));
const Events = lazy(() => import('./pages/Events').then(module => ({ default: module.Events })));
const Services = lazy(() => import('./pages/Services/').then(module => ({ default: module.Services })));
const Contact = lazy(() => import('./pages/Contact').then(module => ({ default: module.Contact })));
const PPAForm = lazy(() => import('./pages/PPAForm').then(module => ({ default: module.PPAForm })));
const DocumentList = lazy(() => import('./pages/DocumentList').then(module => ({ default: module.DocumentList })));
const Contests = lazy(() => import('./pages/Contests').then(module => ({ default: module.Contests })));
const Biddings = lazy(() => import('./pages/Biddings').then(module => ({ default: module.Biddings })));
const DirectHiring = lazy(() => import('./pages/DirectHiring').then(module => ({ default: module.DirectHiring })));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy').then(module => ({ default: module.PrivacyPolicy })));
const Accessibility = lazy(() => import('./pages/Accessibility').then(module => ({ default: module.Accessibility })));
const SiteMap = lazy(() => import('./pages/SiteMap').then(module => ({ default: module.SiteMap })));
const Secretarias = lazy(() => import('./pages/Secretarias/').then(module => ({ default: module.Secretarias })));
const UserProfile = lazy(() => import('./pages/UserProfile').then(module => ({ default: module.UserProfile })));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword').then(module => ({ default: module.ForgotPassword })));
const ResetPassword = lazy(() => import('./pages/ResetPassword').then(module => ({ default: module.ResetPassword })));

// Páginas de Admin
const AdminDashboard = lazy(() => import('./pages/admin/Dashboard').then(module => ({ default: module.AdminDashboard })));
const AdminNews = lazy(() => import('./pages/admin/News').then(module => ({ default: module.AdminNews })));
const FacebookImport = lazy(() => import('./pages/admin/FacebookImport').then(module => ({ default: module.FacebookImport })));
const AdminServices = lazy(() => import('./pages/admin/Services').then(module => ({ default: module.AdminServices })));
const AdminBanners = lazy(() => import('./pages/admin/Banners').then(module => ({ default: module.AdminBanners })));
const AdminDepartments = lazy(() => import('./pages/admin/Departments').then(module => ({ default: module.AdminDepartments })));
const AdminEvents = lazy(() => import('./pages/admin/Events').then(module => ({ default: module.AdminEvents })));
const AdminSecretarias = lazy(() => import('./pages/admin/Secretarias').then(module => ({ default: module.AdminSecretarias })));
const Documents = lazy(() => import('./pages/admin/documents/Documents').then(module => ({ default: module.Documents })));
const AdminCategories = lazy(() => import('./pages/admin/DocumentCategories').then(module => ({ default: module.AdminCategories })));
const AdminSubCategories = lazy(() => import('./pages/admin/DocumentSubCategories').then(module => ({ default: module.AdminSubCategories })));
const QuickLinks = lazy(() => import('./pages/admin/QuickLinks').then(module => ({ default: module.QuickLinks })));
const DocumentAnalysis = lazy(() => import('./pages/admin/DocumentAnalysis').then(module => ({ default: module.DocumentAnalysis })));
const DocumentProcessing = lazy(() => import('./pages/admin/DocumentProcessing').then(module => ({ default: module.DocumentProcessing })));
const AdminAnnouncements = lazy(() => import('./pages/admin/Announcements').then(module => ({ default: module.AdminAnnouncements })));
const AdminContactMessages = lazy(() => import('./pages/admin/ContactMessages/').then(module => ({ default: module.AdminContactMessages })));
const TransparencyImport = lazy(() => import('./pages/admin/TransparencyImport/').then(module => ({ default: module.TransparencyImport })));
const NewsletterSubscribers = lazy(() => import('./pages/admin/NewsletterSubscribers').then(module => ({ default: module.NewsletterSubscribers })));
const AdminConfigs = lazy(() => import('./pages/admin/Configs').then(module => ({ default: module.AdminConfigs })));
const DirectHiringProcesses = lazy(() => import('./pages/admin/DirectHiringProcesses').then(module => ({ default: module.DirectHiringProcesses })));
const AdminUsers = lazy(() => import('./pages/admin/Users').then(module => ({ default: module.AdminUsers })));

// Componente de Redirecionamento (assumindo export nomeado também)
const ServiceRedirect = lazy(() => import('./components/ServiceRedirect').then(module => ({ default: module.ServiceRedirect })));

// Páginas de Erro (mantidas estáticas)
import { NotFound } from './pages/errors/NotFound';
import { ServerError } from './pages/errors/ServerError';
import { Forbidden } from './pages/errors/Forbidden';

// --- Fim das Importações Dinâmicas ---


// Analytics tracker component
function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    trackPageView(location.pathname + location.search);
  }, [location]);

  return null;
}

function App() {
  const { isDark } = useTheme();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Toggle body class when menu is open to prevent scrolling
  useEffect(() => {
    if (isMenuOpen) {
      document.body.classList.add('menu-open');
    } else {
      document.body.classList.remove('menu-open');
    }

    return () => {
      document.body.classList.remove('menu-open');
    };
  }, [isMenuOpen]);

 useEffect(() => {
    // console.log('[App] Initializing application');
    // initGA(); // Mantenha a inicialização de outras libs aqui, se houver

    // REMOVIDO: preloadAuth - O hook useAuthStatus agora cuida da verificação inicial da sessão
    // REMOVIDO: setupTokenHealthMonitor - A função foi deletada

    // A função de cleanup agora está vazia, pois não há mais monitor para limpar
    return () => {
       // console.log('[App] Cleaning up application resources');
    };
  }, []); // Roda apenas uma vez na montagem do App

  return (
    <div className={isDark ? 'dark' : ''}>
      <HelmetProvider>
        <BrowserRouter>
          <ErrorBoundary>
            <ScrollToTop />
            <AnalyticsTracker />
            <div className="min-h-screen flex flex-col bg-gray-100 dark:bg-gray-900 text-secondary-900 dark:text-secondary-100">
              <Header />
              <main className="flex-grow w-full">
                {/* Suspense envolve TODAS as rotas */}
                <Suspense fallback={<LoadingFallback />}>
                  <Routes>
                    {/* Todas as suas <Route ... /> como estavam antes,
                        mas agora usando as constantes lazy (Home, Login, etc.) */}

                    <Route path="/" element={<Home />} />
                    <Route path="/login" element={<Login />} />
                    <Route path="/registrar" element={
                      <RegisterFormProvider>
                        <Register />
                      </RegisterFormProvider>
                    } />
                    <Route path="/esqueceu-senha" element={<ForgotPassword />} />
                    <Route path="/reset-password" element={<ResetPassword />} />
                    <Route path="/noticias" element={<NewsList />} />
                    <Route path="/noticias/:slug" element={<NewsDetail />} />
                    <Route path="/legislacao" element={<Legislation />} />
                    <Route path="/departamentos" element={<Departments />} />
                    <Route path="/secretarias" element={<Secretarias />} />
                    <Route path="/eventos" element={<Events />} />
                    <Route path="/servicos" element={<Services />} />
                    <Route path="/servico/:service" element={<ServiceRedirect />} />
                    <Route path="/contato" element={<Contact />} />
                    <Route path="/ppa-loa-ldo" element={<PPAForm />} />
                    <Route path="/concursos" element={<Contests />} />
                    <Route path="/concursos/:slug" element={<Contests />} />
                    <Route path="/licitacoes" element={<Biddings />} />
                    <Route path="/licitacoes/:slug" element={<Biddings />} />
                    <Route path="/licitacao/contratacao-direta" element={<DirectHiring />} />
                    <Route path="/licitacao/contratacao-direta/:slug" element={<DirectHiring />} />
                    <Route path="/privacidade" element={<PrivacyPolicy />} />
                    <Route path="/acessibilidade" element={<Accessibility />} />
                    <Route path="/mapa-do-site" element={<SiteMap />} />
                    <Route path="/documentos/:category/:subcategory?" element={<DocumentList />} />

                    {/* Rotas de Perfil */}
                    <Route path="/perfil" element={<AuthenticatedRoute />}>
                      <Route index element={<UserProfile />} />
                    </Route>

                    {/* Redirecionamentos */}
                    <Route path="/transparencia" element={<ServiceRedirect />} />
                    <Route path="/esus" element={<ServiceRedirect />} />
                    <Route path="/nfse" element={<ServiceRedirect />} />
                    <Route path="/sipweb" element={<ServiceRedirect />} />
                    <Route path="/servicosweb" element={<ServiceRedirect />} />
                    <Route path="/servicos-online" element={<Services />} />

                    {/* Rotas de Admin */}
                    <Route path="/admin" element={<ProtectedRoute />}>
                        <Route index element={<AdminDashboard />} />
                        <Route path="noticias" element={<AdminNews />} />
                        <Route path="facebook-import" element={<FacebookImport />} />
                        <Route path="servicos" element={<AdminServices />} />
                        <Route path="banners" element={<AdminBanners />} />
                        <Route path="anuncios" element={<AdminAnnouncements />} />
                        <Route path="departamentos" element={<AdminDepartments />} />
                        <Route path="secretarias" element={<AdminSecretarias />} />
                        <Route path="eventos" element={<AdminEvents />} />
                        <Route path="documentos" element={<Documents />} />
                        <Route path="documentos/categorias" element={<AdminCategories />} />
                        <Route path="documentos/subcategorias" element={<AdminSubCategories />} />
                        <Route path="documentos/analise" element={<DocumentAnalysis />} />
                        <Route path="documentos/processamento" element={<DocumentProcessing />} />
                        <Route path="documentos/importacao" element={<TransparencyImport />} />
                        <Route path="links-rapidos" element={<QuickLinks />} />
                        <Route path="mensagens" element={<AdminContactMessages />} />
                        <Route path="newsletter" element={<NewsletterSubscribers />} />
                        <Route path="configs" element={<AdminConfigs />} />
                        <Route path="contratacao-direta" element={<DirectHiringProcesses />} />
                        <Route path="users" element={<AdminUsers />} />
                    </Route>

                    {/* Páginas de Erro */}
                    <Route path="/forbidden" element={<Forbidden />} />
                    <Route path="/server-error" element={<ServerError />} />
                    <Route path="*" element={<NotFound />} />

                  </Routes>
                </Suspense>
              </main>
              <Footer />
            </div>
          </ErrorBoundary>
        </BrowserRouter>
      </HelmetProvider>
    </div>
  );
}

export default App;